import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';

// VERSION MARKER: Performance Optimization v3.2
// If you see this log, the new code with signal deduplication is loaded
// CRITICAL: This prevents 5-6 second delays when loading already-analyzed videos
const VERSION_TIMESTAMP = new Date().toISOString();
console.log(`­ƒÜÇ [CommentSignals v3.2] Performance optimization loaded at ${VERSION_TIMESTAMP}`);
console.log('­ƒÜÇ [CommentSignals v3.2] If you see this, the new code is active. If not, hard refresh your browser (Ctrl+Shift+R)');
console.log('­ƒÜÇ [CommentSignals v3.2] OLD CODE DETECTED if you see "Ô£à [CommentSignals] Saved signal" without "ÔÅ¡´©Å [CommentSignals]" skip messages');

/**
 * Comment Signals Service
 * Manages the commentSignals collection for persistent learning
 */

/**
 * Store or update comment signal data
 * @param {string} commentId - Unique comment ID
 * @param {Object} signalData - Signal data
 * @param {string} signalData.sentiment - "positive" | "neutral" | "negative"
 * @param {string} signalData.intent - "purchase" | "friction" | "question" | "suggestion" | "feedback" | "other"
 * @param {number} signalData.confidence - Confidence score (0-1)
 * @param {string[]} signalData.entities - Extracted entities/topics
 * @param {string} signalData.videoId - Associated video ID
 * @param {string} signalData.channelId - Associated channel ID
 */
export async function saveCommentSignal(commentId, signalData) {
  try {
    const signalRef = doc(db, 'commentSignals', commentId);
    
    // CRITICAL FAIL-SAFE v3.2: Always check if signal exists BEFORE saving
    // This prevents redundant writes even if higher-level checks fail
    // This is the LAST LINE OF DEFENSE against redundant writes
    const existingDoc = await getDoc(signalRef);
    if (existingDoc.exists()) {
      // Signal already exists - skip saving to prevent redundant writes
      // This should happen for ALL existing signals, preventing the "Saved signal" logs
      if (!signalData._silentSkip) {
        console.log(`ÔÅ¡´©Å [CommentSignals v3.2] Signal already exists for comment: ${commentId} - SKIPPING save (preventing redundant write)`);
      }
      return existingDoc.data();
    }
    
    // Only reach here if signal does NOT exist - safe to save
    const data = {
      sentiment: signalData.sentiment || 'neutral',
      intent: signalData.intent || 'other',
      confidence: signalData.confidence || 0.5,
      entities: signalData.entities || [],
      videoId: signalData.videoId,
      channelId: signalData.channelId,
      classifiedAt: new Date(),
      lastUpdated: new Date()
    };

    await setDoc(signalRef, data, { merge: true });
    // Only log new signals (not redundant ones)
    console.log(`Ô£à [CommentSignals v3.2] Saved NEW signal for comment: ${commentId}`);
    return data;
  } catch (error) {
    console.error('ÔØî [CommentSignals v3.2] Error saving signal:', error);
    throw error;
  }
}

/**
 * Update confidence score for a comment signal
 * @param {string} commentId - Comment ID
 * @param {number} newConfidence - Updated confidence score
 */
export async function updateConfidence(commentId, newConfidence) {
  try {
    const signalRef = doc(db, 'commentSignals', commentId);
    await updateDoc(signalRef, {
      confidence: Math.min(1, Math.max(0, newConfidence)),
      lastUpdated: new Date()
    });
    console.log(`Ô£à [CommentSignals] Updated confidence for ${commentId}: ${newConfidence}`);
  } catch (error) {
    console.error('ÔØî [CommentSignals] Error updating confidence:', error);
    throw error;
  }
}

/**
 * Get comment signal by ID
 * @param {string} commentId - Comment ID
 * @returns {Object|null} Signal data or null
 */
export async function getCommentSignal(commentId) {
  try {
    const signalRef = doc(db, 'commentSignals', commentId);
    const signalDoc = await getDoc(signalRef);
    
    if (signalDoc.exists()) {
      return { id: signalDoc.id, ...signalDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('ÔØî [CommentSignals] Error getting signal:', error);
    return null;
  }
}

/**
 * Fast check if ANY signals exist for a video (stops after first match)
 * @param {string} videoId - Video ID
 * @returns {Promise<boolean>} True if at least one signal exists
 */
export async function hasSignalsForVideo(videoId) {
  try {
    const signalsRef = collection(db, 'commentSignals');
    const q = query(signalsRef, where('videoId', '==', videoId), limit(1));
    const querySnapshot = await getDocs(q);
    const hasSignals = !querySnapshot.empty;
    console.log(`­ƒöì [CommentSignals v3.2] Fast check: ${hasSignals ? 'SIGNALS EXIST' : 'NO SIGNALS'} for video: ${videoId}`);
    return hasSignals;
  } catch (error) {
    console.error('ÔØî [CommentSignals v3.2] Error checking signals:', error);
    return false;
  }
}

/**
 * Get all signals for a video
 * @param {string} videoId - Video ID
 * @returns {Array} Array of signal objects
 */
export async function getSignalsForVideo(videoId) {
  try {
    const signalsRef = collection(db, 'commentSignals');
    const q = query(signalsRef, where('videoId', '==', videoId));
    const querySnapshot = await getDocs(q);
    
    const signals = [];
    querySnapshot.forEach((doc) => {
      signals.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`­ƒôè [CommentSignals v3.2] Retrieved ${signals.length} signals for video: ${videoId}`);
    return signals;
  } catch (error) {
    console.error('ÔØî [CommentSignals v3.2] Error getting video signals:', error);
    return [];
  }
}

/**
 * Get all signals for a channel
 * @param {string} channelId - Channel ID
 * @returns {Array} Array of signal objects
 */
export async function getSignalsForChannel(channelId) {
  try {
    const signalsRef = collection(db, 'commentSignals');
    const q = query(signalsRef, where('channelId', '==', channelId));
    const querySnapshot = await getDocs(q);
    
    const signals = [];
    querySnapshot.forEach((doc) => {
      signals.push({ id: doc.id, ...doc.data() });
    });
    
    console.log(`­ƒôè [CommentSignals] Retrieved ${signals.length} signals for channel: ${channelId}`);
    return signals;
  } catch (error) {
    console.error('ÔØî [CommentSignals] Error getting channel signals:', error);
    return [];
  }
}

/**
 * Batch save multiple comment signals
 * @param {Array} signals - Array of { commentId, signalData }
 */
export async function batchSaveSignals(signals) {
  if (!signals || signals.length === 0) {
    return;
  }

  try {
    // CRITICAL PERFORMANCE FIX v3.2: Check which signals already exist BEFORE processing
    // This prevents redundant Firestore writes that cause 5-6 second delays
    console.log(`­ƒöì [CommentSignals v3.2] Batch check: Checking ${signals.length} signals for existing documents...`);
    
    const commentIds = signals.map(s => s.commentId);
    const existingSignalsRefs = commentIds.map(id => doc(db, 'commentSignals', id));
    const existingDocs = await Promise.all(existingSignalsRefs.map(ref => getDoc(ref)));
    
    const existingCommentIds = new Set();
    existingDocs.forEach((docSnap, index) => {
      if (docSnap.exists()) {
        existingCommentIds.add(commentIds[index]);
      }
    });

    console.log(`­ƒöì [CommentSignals v3.2] Found ${existingCommentIds.size} existing signals out of ${signals.length} total`);

    // Filter out signals that already exist
    const newSignals = signals.filter(s => !existingCommentIds.has(s.commentId));
    const skippedCount = signals.length - newSignals.length;

    if (skippedCount > 0) {
      console.log(`ÔÅ¡´©Å [CommentSignals v3.2] Skipping ${skippedCount} existing signals (out of ${signals.length} total) - preventing redundant writes`);
    }

    if (newSignals.length === 0) {
      console.log(`ÔÜí [CommentSignals v3.2] All ${signals.length} signals already exist - NO WRITES NEEDED (performance optimized)`);
      return;
    }

    // Mark signals for silent skip in saveCommentSignal (to reduce log noise)
    const promises = newSignals.map(({ commentId, signalData }) =>
      saveCommentSignal(commentId, { ...signalData, _silentSkip: true })
    );
    
    await Promise.all(promises);
    console.log(`Ô£à [CommentSignals v3.2] Batch saved ${newSignals.length} new signals (skipped ${skippedCount} existing)`);
  } catch (error) {
    console.error('ÔØî [CommentSignals] Error batch saving signals:', error);
    throw error;
  }
}

